import React, { useCallback, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import IconButton from '@atlaskit/button/icon/button';
import CrossIcon from '@atlaskit/icon/core/cross';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { Card as SmartCard } from '@atlaskit/smart-card/card/lazy';
import { SnippetBlock } from '@atlaskit/smart-card/flexible/snippet-block';
import { TitleBlock } from '@atlaskit/smart-card/flexible/title-block';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import { CONTEXT_PATH } from '@confluence/named-routes/entry-points/context.ts';
import { ConfluenceSmartCardProvider } from '@confluence/smart-card-provider/entry-points/ConfluenceSmartCardProvider.tsx';

import { SourceLogoStack } from '@atlassian/rovo-platform-ui-components/source-logo-stack';

import { SourceItemContent } from './SourceItem.tsx';
import type { Source } from './PopularQuestions.tsx';

type SourcesSectionProps = {
	sources: readonly Source[];
	spaceKey: string;
	questionPosition: number;
};

export const SourcesSection = ({
	sources,
	spaceKey,
	questionPosition,
}: SourcesSectionProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const { Popup } = useChoreographerAssets();
	const [isOpen, setIsOpen] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const validSources = useMemo(() => sources.filter((source) => source.content != null), [sources]);

	const sourceUrls = useMemo(
		() =>
			validSources.flatMap((source) => {
				const webui = source.content?.links?.webui;
				return webui ? [`${window.location.origin}${CONTEXT_PATH}${webui}`] : [];
			}),
		[validSources],
	);

	const fireSourcesEvent = useCallback(
		(action: 'expanded' | 'collapsed') => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action,
					actionSubject: 'popup',
					actionSubjectId: 'sources',
					source: 'smartSpaceOverview',
					attributes: {
						moduleType: 'faq',
						spaceKey,
						questionPosition,
						sourceCount: validSources.length,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent, spaceKey, questionPosition, validSources],
	);

	const handleToggle = useCallback(
		(event: React.MouseEvent) => {
			event.stopPropagation();
			setIsOpen((prev) => {
				fireSourcesEvent(prev ? 'collapsed' : 'expanded');
				return !prev;
			});
		},
		[fireSourcesEvent],
	);

	const handleClose = useCallback(() => {
		setIsOpen(false);
		fireSourcesEvent('collapsed');
	}, [fireSourcesEvent]);

	if (validSources.length === 0) {
		return null;
	}

	return (
		<Box xcss={styles.triggerWrapper}>
			<Popup
				placement="bottom-end"
				isOpen={isOpen}
				onClose={handleClose}
				content={() => (
					<ConfluenceSmartCardProvider authFlow="disabled">
						<Box xcss={styles.popupContent}>
							<Inline
								space="space.0"
								spread="space-between"
								alignBlock="center"
								xcss={styles.popupHeader}
							>
								<Box xcss={styles.popupHeaderText}>
									<Text size="medium" weight="medium">
										{formatMessage(i18n.sourcesCount, { count: validSources.length })}
									</Text>
								</Box>
								<IconButton
									appearance="subtle"
									icon={(props) => <CrossIcon {...props} size="small" />}
									label={formatMessage(i18n.closeSources)}
									shape="circle"
									onClick={handleClose}
								/>
							</Inline>
							<Stack as="ul" xcss={styles.sourcesList} space="space.050">
								{validSources.map((source, index) => {
									const webui = source.content?.links?.webui;
									const absoluteUrl = webui
										? `${window.location.origin}${CONTEXT_PATH}${webui}`
										: undefined;

									return (
										<li key={source.content?.id}>
											<Pressable
												xcss={styles.sourceListItem}
												onClick={(e) => {
													e.stopPropagation();
													createAnalyticsEvent({
														type: 'sendUIEvent',
														data: {
															action: 'clicked',
															actionSubject: 'link',
															actionSubjectId: 'sourceLink',
															source: 'smartSpaceOverview',
															attributes: {
																moduleType: 'faq',
																spaceKey,
																questionPosition,
																sourceIndex: index,
																sourceProduct: source.content?.embeddedProduct ?? null,
															},
														},
													}).fire();
													if (absoluteUrl) {
														window.open(absoluteUrl, '_blank', 'noopener,noreferrer');
													}
												}}
											>
												{absoluteUrl ? (
													<SmartCard
														appearance="block"
														url={absoluteUrl}
														showHoverPreview={false}
														fallbackComponent={() => <SourceItemContent source={source} />}
														ui={{
															hideBackground: true,
															hideElevation: true,
															hidePadding: true,
														}}
													>
														<TitleBlock maxLines={1} />
														<SnippetBlock maxLines={2} />
													</SmartCard>
												) : (
													<SourceItemContent source={source} />
												)}
											</Pressable>
										</li>
									);
								})}
							</Stack>
						</Box>
					</ConfluenceSmartCardProvider>
				)}
				trigger={(triggerProps) => (
					<Pressable
						{...triggerProps}
						onClick={handleToggle}
						xcss={cx(styles.sourcesButton, isOpen && styles.sourcesButtonSelected)}
					>
						<Inline space="space.075" alignBlock="center">
							<SourceLogoStack sources={sourceUrls} />
							<Text
								size="medium"
								weight="medium"
								color={isOpen ? 'color.text.selected' : 'color.text.subtle'}
							>
								{formatMessage(i18n.sourcesCount, { count: validSources.length })}
							</Text>
						</Inline>
					</Pressable>
				)}
			/>
		</Box>
	);
};

const i18n = defineMessages({
	sourcesCount: {
		id: 'space-overview.smart-space-overview.popular-questions.sources-count',
		defaultMessage: '{count, plural, one {# Source} other {# Sources}}',
		description: 'Button label showing the number of sources for a popular question.',
	},
	closeSources: {
		id: 'space-overview.smart-space-overview.popular-questions.close-sources',
		defaultMessage: 'Close sources',
		description: 'Accessible label for the button to close the sources popup.',
	},
});

const styles = cssMap({
	sourcesButton: {
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
		paddingTop: token('space.0'),
		paddingBottom: token('space.0'),
		height: '30px',
		borderColor: token('color.border'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderRadius: token('radius.large'),
		backgroundColor: token('color.background.neutral.subtle'),
		color: token('color.text.subtle'),
		fontWeight: token('font.weight.medium'),
		fontFamily: token('font.family.body'),
		cursor: 'pointer',
		display: 'flex',
		alignItems: 'center',
		gap: token('space.050'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	sourcesButtonSelected: {
		borderColor: token('color.border.selected'),
		backgroundColor: token('color.background.selected'),
		color: token('color.text.selected'),
		'&:hover': {
			backgroundColor: token('color.background.selected.hovered'),
		},
	},
	triggerWrapper: {
		display: 'inline-block',
		flexShrink: 0,
	},
	popupContent: {
		paddingTop: token('space.100'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.150'),
		width: '330px',
		maxHeight: '400px',
		overflowY: 'auto',
		borderRadius: token('radius.xlarge'),
	},
	popupHeader: {
		marginBottom: token('space.025'),
	},
	popupHeaderText: {
		paddingLeft: token('space.100'),
	},
	sourcesList: {
		paddingLeft: token('space.0'),
		listStyle: 'none',
		margin: 0,
	},
	sourceCard: {
		border: 'none',
		textAlign: 'left',
		overflow: 'hidden',
		width: '100%',
	},
	sourceListItem: {
		borderRadius: token('radius.medium'),
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		cursor: 'pointer',
		backgroundColor: 'transparent',
		width: '100%',
		textAlign: 'left',
		overflow: 'hidden',
		transition: token('motion.listitem.hovered'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
});
